import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSound } from "@/context/SoundContext";
import { toast } from "sonner";

// Polls active transactions + subscribes to WebSocket for instant refresh.
export function useTransactions() {
  const { currentStore } = useAuth();
  const sound = useSound();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const mounted = useRef(true);
  const wsRef = useRef(null);
  const overdueSeen = useRef(null);

  const load = useCallback(async () => {
    if (!currentStore) return;
    try {
      const { data } = await api.get(`/transactions?store_id=${currentStore.id}`);
      if (mounted.current) setTxns(data.transactions || []);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [currentStore]);

  useEffect(() => {
    mounted.current = true;
    load();
    // Fallback poll (slower, since WS drives instant updates)
    const id = setInterval(load, 12000);
    return () => { mounted.current = false; clearInterval(id); };
  }, [load]);

  // WebSocket subscription
  useEffect(() => {
    if (!currentStore) return;
    const token = localStorage.getItem("tb_token");
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    let closed = false;
    let reconnectTimer = null;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(`${base}/api/ws?token=${token}&store_id=${currentStore.id}`);
      wsRef.current = ws;
      ws.onopen = () => setLive(true);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "update") load();
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setLive(false);
        if (!closed) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    };
  }, [currentStore, load]);

  useEffect(() => {
    const overdueList = txns.filter((t) => t.is_overdue);
    sound.setOverdueCount(overdueList.length);
    const currentIds = new Set(overdueList.map((t) => t.id));
    if (overdueSeen.current === null) {
      // First load: seed baseline without toasting existing overdue items.
      overdueSeen.current = currentIds;
      return;
    }
    overdueList.forEach((t) => {
      if (!overdueSeen.current.has(t.id)) {
        toast.error(`Overdue: KIN ${t.kin_number}`, {
          description: `${t.stage_label || t.current_status} has exceeded its time target.`,
          duration: 8000,
        });
      }
    });
    overdueSeen.current = currentIds;
  }, [txns, sound]);

  return { txns, loading, reload: load, live };
}

export function Column({ title, count, accent, children, testId }) {
  return (
    <div className="flex flex-col bg-[#0d0d0d] border border-border min-h-[200px]" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-[#0d0d0d] z-10">
        <span className="font-display text-xl font-bold uppercase tracking-wide">{title}</span>
        <span className="font-mono-t text-sm px-2 py-0.5 rounded-sm" style={{ background: accent + "22", color: accent }}>{count}</span>
      </div>
      <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-190px)]">
        {children}
        {count === 0 && <div className="text-center text-muted-foreground text-sm py-8">No transactions</div>}
      </div>
    </div>
  );
}
